(ns app.front.view.group.reporting.permit
  (:require [app.front.ui :as ui]
            [app.front.ui.content-switcher :as ui-cs]
            [app.front.ui.filter-common :as filter-common]
            [app.front.ui.section :as section]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.group.reporting.common :as reporting-common]
            [app.front.view.group.reporting.download-button :as rdb]
            [app.front.view.group.reporting.filters :as reporting-filters]
            [app.front.view.group.reporting.form-fields :as reporting-ff]
            [app.front.view.group.reporting.list :as report-list]
            [app.front.view.group.reporting.property-attributes :as reporting-pa]
            [app.query.filter-util :as filter-util]
            [app.reporting.model :as reporting-model]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))


(defn optional-fields [permit-types processing-times-enabled?]
  (if (contains? permit-types :permit-type/L)
    (concat [:reporting/municipality]
            (when (ff/enabled? :feature-flag.id/express-issuance)
              [:reporting/express-issuance]))
    (concat (when (ff/enabled? :feature-flag.id/property-db-units)
              [:reporting/application-unit-type-and-number])
            [:reporting/municipality
             :reporting/applicant-address
             :reporting/applicant-company
             :reporting/agent-name
             :reporting/agent-address
             :reporting/agent-company
             :reporting/owner-address
             :reporting/owner-company
             :reporting/builder-name
             :reporting/builder-address
             :reporting/builder-company
             :reporting/permit-fees-total
             :reporting/construction-cost
             :reporting/area-of-work
             :reporting/statcan-building-code
             :reporting/statcan-work-code
             :reporting/assignee
             :reporting/draft-report-release-date
             :reporting/draft-report-review-date
             :reporting/ready-for-approval-date
             :reporting/committee-meeting-name
             :reporting/committee-meeting-date
             :reporting/review-start
             :reporting/review-end
             :reporting/finished-date
             :reporting/time-in-review
             :reporting/changes-requested
             :reporting/municipal-review]
            (when processing-times-enabled?
              [:reporting/decision-made
               :reporting/completion-due-date
               :reporting/decision-due-date
               :reporting/days-to-completion
               :reporting/days-to-decision])
            (when (ff/enabled? :feature-flag.id/express-issuance)
              [:reporting/express-issuance]))))


(def sort-by-options
  [:reporting/submitted-date
   :reporting/issued-date])


(defn default-settings [default-report-type]
  {:categories                   #{}
   :sort-by                      #{(-> sort-by-options first)}
   :report-type                  #{default-report-type}
   :selected-form-fields         nil
   :selected-optional-fields     #{}
   :party-roles                  #{}
   :property-attribute-templates []
   :date-selector-mode           #{:reporting.date-range-options/current-month}})


(rf/reg-event-db ::set-selected
  (fn [db [_ k v]]
    (assoc-in db [::data k] v)))


(rf/reg-sub ::data :-> ::data)


(rf/reg-sub ::permit-report-types
  :<- [::gas/group-permit-types]
  :<- [::gas/category-group-configs :permit-type/P]
  (fn [[permit-types p-cats] _]
    (reporting-model/permit-report-types permit-types p-cats)))

(rf/reg-sub ::default-report-type
  :<- [::permit-report-types]
  :-> first)


(rf/reg-sub ::report-type
  :<- [::data]
  :<- [::default-report-type]
  (fn [[data default-report-type] _]
    (or (::report-type data)
        default-report-type)))


(rf/reg-sub ::sort-option
  :<- [::data]
  (fn [data _]
    (or (some->> data
                 ::sort-option
                 ((set sort-by-options)))
        (first sort-by-options))))


(rf/reg-sub
  ::field-tab-selector-index
  :<- [::data]
  (fn [data _]
    (get data ::field-tab-selector-index 0)))


(rf/reg-event-fx ::select-saved-filter
  (fn [{:keys [db]} [_ selected-filter]]
    (let [filter-values      (filter-util/filter-entity->search-values-map selected-filter)
          type               (->> filter-values :report-type first)
          sort               (->> filter-values :sort-by first)
          date-selector-mode (->> filter-values :date-selector-mode first)]
      {:dispatch-n [[::reporting-common/set-selected-categories (:categories filter-values)]
                    [::reporting-common/set-selected-party-roles (:party-roles filter-values)]]
       :db         (-> db
                       (update ::data #(p/assoc-if %
                                                   ::report-type type
                                                   ::sort-option sort))
                       (p/assoc-if ::reporting-common/date-selector-mode date-selector-mode
                                   ::reporting-ff/selected (->> (:form-fields filter-values)
                                                                (map-indexed vector)
                                                                (into {}))
                                   ::reporting-common/selected-optional-fields (:optional-fields filter-values)
                                   ::reporting-pa/selected-attributes (:property-attribute-templates filter-values)))})))


(rf/reg-event-db ::initialize-settings
  (fn [db [_ {:keys [default-report-type]}]]
    (let [{:keys [categories
                  sort-by
                  report-type
                  selected-form-fields
                  selected-optional-fields
                  party-roles
                  property-attribute-templates
                  date-selector-mode]}         (default-settings default-report-type)]
      (-> db
          (update ::data #(assoc %
                                 ::report-type (first report-type)
                                 ::sort-option (first sort-by)))
          (assoc ::reporting-common/date-selector-mode (first date-selector-mode)
                 ::reporting-common/selected-categories categories
                 ::reporting-ff/selected selected-form-fields
                 ::reporting-common/selected-optional-fields selected-optional-fields
                 ::reporting-common/selected-party-roles party-roles
                 ::reporting-pa/selected-attributes property-attribute-templates)))))


(rf/reg-sub ::filter-settings
  :<- [::gas/group]
  :<- [::reporting-common/selected-categories]
  :<- [::reporting-common/selected-optional-fields]
  :<- [::report-type]
  :<- [::sort-option]
  :<- [::field-tab-selector-index]
  :<- [::reporting-common/date-selector-mode]
  :<- [::reporting-ff/sorted-selected-fields]
  :<- [::reporting-common/selected-party-roles]
  :<- [::reporting-pa/selected-attributes]
  (fn [[group categories optional-fields report-type sort-by field-tab-idx
        date-selector-mode form-fields party-roles property-attributes] _]
    {:report-type                  #{report-type}
     :sort-by                      #{sort-by}
     :field-tab-idx                #{field-tab-idx}
     :date-selector-mode           #{date-selector-mode}
     :group-id                     #{(:db/id group)}
     :categories                   categories
     :optional-fields              optional-fields
     :form-fields                  form-fields
     :party-roles                  party-roles
     :property-attribute-templates property-attributes}))


(rf/reg-sub ::filters-in-default-state?
  :<- [::filter-settings]
  :<- [::default-report-type]
  (fn [[filter-settings default-report-type]]
    (= (dissoc filter-settings :group-id) (default-settings default-report-type))))


(rf/reg-sub ::conditions
  :<- [::gas/group-id]
  :<- [::filter-settings]
  (fn [[group-id filter-settings]]
    (-> filter-settings
        (assoc :group-id #{group-id})
        (filter-common/filter-values->conditions #{:form-fields :property-attribute-templates}))))


(rf/reg-sub ::query
  :<- [::gas/group-id]
  :<- [::report-type]
  :<- [::reporting-common/selected-categories]
  :<- [::reporting-common/selected-optional-fields]
  :<- [::reporting-ff/sorted-selected-fields]
  :<- [::reporting-common/date-range]
  :<- [::sort-option]
  :<- [::reporting-common/selected-party-roles]
  :<- [::reporting-pa/selected-attributes]
  (fn [[group-id report-type categories optional-fields
        form-fields [start-date end-date] sort-option party-roles property-attributes] _]
    (-> {:group-id    group-id
         :report-type report-type}
        (p/assoc-if :categories (seq categories)
                    :optional-fields optional-fields
                    :form-fields form-fields
                    :start-date (when-not (= report-type :reporting/open-permits) start-date)
                    :end-date (when-not (= report-type :reporting/open-permits) end-date)
                    :party-roles (seq party-roles)
                    :sort-by sort-option
                    :property-attribute-templates property-attributes))))


(rf/reg-sub ::property-attributes-enabled?
  :<- [::ff/enabled? :feature-flag.id/reporting-property-attributes]
  :<- [::reporting-common/features]
  (fn [[feature-enabled? features]]
    (and feature-enabled? (:property-management? features))))


(defn sort-selector [tr]
  (let [sort-option (<sub [::sort-option])]
    [:div.flex.items-center.mb-6
     [:span.mr-4.whitespace-nowrap (tr [:reporting/sort-by])]
     [ui-cs/content-switcher {:class     "mt-2 mb-2"
                              :items     sort-by-options
                              :val->str  (comp tr vector)
                              :current   sort-option
                              :on-change #(>evt [::set-selected ::sort-option %])}]]))


(defn view [match]
  (let [{:keys [field-tab-idx] :as settings} (<sub [::filter-settings])
        tr                                   (<sub [::gas/group-tr])
        report-type                          (<sub [::report-type])
        query                                (<sub [::query])
        tab-label                            #(str (tr [%1]) " " (tr [:reporting/number-selected] [(-> settings %2 count)]))
        permit-types                         (<sub [::gas/group-permit-types])
        report-types                         (<sub [::permit-report-types])
        processing-times?                    (<sub [::gas/group-or-descendant-has-feature? :group.feature/target-processing-times])
        invalid-forms?                       (not-empty (<sub [::reporting-ff/invalid-form-selections]))]
    [reporting-common/report-page
     match
     tr
     [:section
      [reporting-filters/filters tr {:filter-type                :filter.type/reporting.permits
                                     :filter-settings-sub        ::filter-settings
                                     :default-filters-sub        ::filters-in-default-state?
                                     :conditions-sub             ::conditions
                                     :select-saved-filter-evt    ::select-saved-filter
                                     :initialize-settings-evt    ::initialize-settings
                                     :initialize-settings-params {:default-report-type (<sub [::default-report-type])}}]]
     [:section
      [:h3 (tr [:reporting/select-report])]
      [reporting-common/report-type-selector
       {:tr                  tr
        :label-loc           :reporting/report-type
        :on-change           #(>evt [::set-selected ::report-type (nth report-types %)])
        :report-type-options report-types
        :selected-index      (p/find-index report-types #(= % report-type))}]
      [:div.relative
       {:style {:padding-right "15rem"}}
       [:div.flex.flex-wrap
        (if-not (contains? (:report-type settings) :reporting/open-permits)
          [reporting-common/date-range-selector tr "csv-date-range" reporting-common/date-range-options]
          [:div.text-gray-600.mt-6.pb-12 (tr [:reporting/range-not-available])])]]]
     (when invalid-forms?
       [ui/warning (tr [:reporting/missing-form-title])])
     [section/tabbed-section
      {:selected-tab-index (or (first field-tab-idx) 0)
       :on-change          #(>evt [::set-selected ::field-tab-selector-index %])
       :tabs               (cond-> [{:label-text (tab-label :reporting/permit-categories :categories)
                                     :content    [reporting-common/category-selector tr false]}
                                    {:label-text (tab-label :reporting/form-fields :form-fields)
                                     :content    [reporting-ff/form-field-selector tr false]}
                                    {:label-text (tab-label :reporting/related-parties :party-roles)
                                     :content    [reporting-common/related-parties-selector tr]}
                                    {:label-text (tab-label :reporting/optional-fields :optional-fields)
                                     :content    [reporting-common/optional-field-selector tr :permits
                                                  (optional-fields permit-types processing-times?)]}]
                             (<sub [::property-attributes-enabled?])
                             (conj {:label-text (tab-label :reporting/property-attributes :property-attribute-templates)
                                    :content    [reporting-pa/property-attribute-selector tr]}))}]
     [sort-selector tr]
     [report-list/list-section (util/mmap tr report-type)]
     [rdb/generate-button (util/mmap query tr)]]))


(def route
  {:name        :view/group.reporting.permits
   :view        view
   :parameters  {:path {:group-id s/Int}}
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [params]
                                (let [group-id (-> params :path :group-id)]
                                  (>evt [::reporting-filters/fetch-saved-filters group-id])
                                  (>evt [::reporting-common/init group-id (first reporting-common/date-range-options)])))
                  :stop       (fn []
                                (>evt [::initialize-settings]))}]})

(def applications-route
  (merge route
         {:name :view/group.reporting.applications}))

(def licences-route
  (merge route
         {:name :view/group.reporting.licences}))
